/**
 * Login page · P2.8 design polish
 *
 * Dev-mode login (AUTH_MODE=dev seed user → SPA JWT). v0.2 swaps to Clerk.
 *
 * Sketch theme · uses design-system semantic tokens (colors / shadows /
 * fonts). NO hardcoded colors anymore — themes can be swapped at runtime
 * via [data-theme="dark"|"clean"] without touching this file.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, type CSSProperties } from 'react';
import { backendUrl } from './workspace.$workspaceId.canvas.$sessionId';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [email, setEmail] = useState('dev@aiw.local');
  const [status, setStatus] = useState<'idle' | 'pending' | 'error' | 'success'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [redirectWorkspaceId, setRedirectWorkspaceId] = useState<string | null>(null);

  // Auto-redirect on success → workspace hub. The hub is the safer beta entry:
  // users can choose an existing session, create a clean one, or manage agents.
  useEffect(() => {
    if (status !== 'success' || !redirectWorkspaceId) return;
    const t = setTimeout(() => {
      navigate({
        to: '/workspace/$workspaceId',
        params: { workspaceId: redirectWorkspaceId },
      });
    }, 600);
    return () => clearTimeout(t);
  }, [status, navigate, redirectWorkspaceId]);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (status === 'pending') return;
    setStatus('pending');
    setError(null);
    try {
      // P3-deploy fix · use absolute backendUrl so SPA hosted on Pages
      // (separate origin from worker) hits the worker, not Pages itself
      // (which would 405). Dev mode (empty backendUrl) keeps Vite proxy.
      const base = backendUrl({});
      const res = await fetch(`${base}/api/auth/login`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setStatus('error');
        setError((body as { error?: string }).error ?? `HTTP ${res.status}`);
        return;
      }
      const body = (await res.json()) as {
        token: string;
        user: { user_id: string; email: string; display_name?: string };
        workspace_id: string;
        role: string;
      };
      sessionStorage.setItem('aiw.spa.token', body.token);
      sessionStorage.setItem('aiw.spa.user', JSON.stringify(body.user));
      sessionStorage.setItem('aiw.spa.workspace_id', body.workspace_id);
      setRedirectWorkspaceId(body.workspace_id);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Network error');
    }
  }

  return (
    <div style={shellStyle}>
      <style>{spinnerKeyframes}</style>
      <div style={cardStyle}>
        <div style={brandStyle}>
          <span style={brandMarkStyle} aria-hidden="true">α</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <h1 style={brandTitleStyle}>ai-workflow</h1>
            <span style={brandTaglineStyle}>异构 agent 协作工作台</span>
          </div>
        </div>

        <div style={dividerStyle} aria-hidden="true" />

        <form onSubmit={handleSubmit} style={formStyle} aria-busy={status === 'pending'}>
          <label style={labelStyle}>
            <span>邮箱</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="email"
              spellCheck={false}
              disabled={status === 'pending' || status === 'success'}
              style={inputStyle}
            />
          </label>

          <button
            type="submit"
            disabled={status === 'pending' || status === 'success'}
            style={primaryBtnStyle(status)}
          >
            {status === 'pending' && <Spinner />}
            {status === 'success'
              ? '已登录 · 进入工作台…'
              : status === 'pending'
              ? '正在登录…'
              : '登录'}
          </button>

          <div style={statusRowStyle} aria-live="polite">
            {status === 'error' && (
              <p style={errorTextStyle}>
                <span aria-hidden="true">✘</span> {error ?? '未知错误'}
              </p>
            )}
            {status === 'success' && (
              <p style={successTextStyle}>
                <span aria-hidden="true">✓</span> token 已存 · 即将进入工作区
              </p>
            )}
            {status === 'idle' && (
              <p style={hintTextStyle}>
                Dev 模式 · 默认 seed 用户 <code style={inlineCodeStyle}>dev@aiw.local</code>
              </p>
            )}
          </div>
        </form>
      </div>

      <p style={footerStyle}>v0.1 dogfood · sketch theme</p>
    </div>
  );
}

function Spinner(): JSX.Element {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        border: '2px solid currentColor',
        borderTopColor: 'transparent',
        borderRadius: '50%',
        animation: 'aiw-spin 0.7s linear infinite',
        marginRight: 8,
        verticalAlign: '-1px',
      }}
    />
  );
}

const spinnerKeyframes = `@keyframes aiw-spin { to { transform: rotate(360deg); } }`;

// === Styles · pure design tokens, no hardcoded colors ===

const shellStyle: CSSProperties = {
  width: '100%',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--space-12)',
  gap: 'var(--space-6)',
  background: 'var(--bg-base)',
  boxSizing: 'border-box',
};

const cardStyle: CSSProperties = {
  width: '100%',
  maxWidth: 380,
  background: 'var(--bg-surface)',
  border: '2px solid var(--border-default)',
  borderRadius: 'var(--radius-2xl)',
  padding: 'var(--space-12) var(--space-12) var(--space-10)',
  boxShadow: 'var(--shadow-card)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-8)',
  // Slight rotation gives the sketch theme its hand-drawn personality
  transform: 'rotate(-0.3deg)',
};

const brandStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-6)',
};

const brandMarkStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 44,
  height: 44,
  borderRadius: 'var(--radius-pill)',
  border: '2px solid var(--border-default)',
  background: 'var(--accent-primary-bg)',
  color: 'var(--accent-primary)',
  fontFamily: 'var(--font-title)',
  fontSize: 26,
  fontWeight: 700,
  lineHeight: 1,
  boxShadow: 'var(--shadow-pop)',
};

const brandTitleStyle: CSSProperties = {
  fontFamily: 'var(--font-title)',
  fontSize: 28,
  lineHeight: 1.1,
  color: 'var(--fg-default)',
};

const brandTaglineStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  color: 'var(--fg-soft)',
};

const dividerStyle: CSSProperties = {
  height: 1,
  background:
    'repeating-linear-gradient(90deg, var(--border-soft) 0 6px, transparent 6px 10px)',
};

const formStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-6)',
};

const labelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  color: 'var(--fg-soft)',
};

const inputStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 16,
  padding: '10px 12px',
  background: 'var(--bg-base)',
  border: '2px solid var(--border-default)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--fg-default)',
  outline: 'none',
  transition: 'box-shadow 0.15s ease',
};

function primaryBtnStyle(status: string): CSSProperties {
  const isSuccess = status === 'success';
  return {
    padding: '10px 16px',
    fontFamily: 'var(--font-body)',
    fontSize: 15,
    fontWeight: 600,
    background: isSuccess ? 'var(--accent-success)' : 'var(--accent-primary)',
    color: 'var(--accent-primary-fg)',
    border: '2px solid var(--border-default)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-pop)',
    transition: 'transform 0.1s ease, box-shadow 0.1s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

const statusRowStyle: CSSProperties = {
  minHeight: 24,
  margin: 0,
};

const errorTextStyle: CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: 'var(--accent-danger)',
  display: 'flex',
  gap: 6,
  alignItems: 'center',
};

const successTextStyle: CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: 'var(--accent-success)',
  display: 'flex',
  gap: 6,
  alignItems: 'center',
};

const hintTextStyle: CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: 'var(--fg-faint)',
};

const inlineCodeStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  padding: '1px 6px',
  background: 'var(--bg-muted)',
  borderRadius: 'var(--radius-xs)',
};

const footerStyle: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  color: 'var(--fg-faint)',
};
